import { useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';

import { Regions } from '../../constants/states';
import { ChevronDown, ChevronRight } from '../atoms/chevrons';
import { StyledDropdown, StyledToggle, FlyoutMenu, GroupsPane, GroupItem, StatesPane } from './atoms';

interface Props {
  value: string;
  onSelect: (regionCode: string) => void;
}

export function RegionSelector({ value, onSelect }: Props) {
  const [show, setShow] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);

  const selectedLabel = useMemo(() => {
    const lower = value.toLowerCase();
    const match = Regions.flatMap((r) => r.states).find((s) => s.region === lower);

    return match?.state ?? '';
  }, [value]);

  const close = () => {
    setShow(false);
    setExpandedGroup(null);
  };

  const handleSelect = (regionCode: string) => {
    onSelect(regionCode);
    close();
  };

  const handleToggle = (isOpen: boolean) => {
    if (isOpen) {
      setShow(true);
    } else {
      close();
    }
  };

  const expandedStates = expandedGroup
    ? Regions.find((r) => r.label === expandedGroup)?.states
    : undefined;

  return (
    <StyledDropdown show={show} onToggle={handleToggle}>
      <StyledToggle variant="outline-secondary">
        {selectedLabel || 'Select a state'}
        <ChevronDown />
      </StyledToggle>

      <FlyoutMenu>
        <GroupsPane>
          {Regions.map((r) => (
            <GroupItem
              key={r.label}
              type="button"
              $active={r.label === expandedGroup}
              onClick={() => setExpandedGroup(r.label)}
              aria-label={`Expand ${r.label} region`}
              aria-expanded={r.label === expandedGroup}
            >
              {r.label} <ChevronRight />
            </GroupItem>
          ))}
        </GroupsPane>

        {expandedStates && (
          <StatesPane>
            {expandedStates.map((s) => (
              <Dropdown.Item
                key={s.region}
                active={s.region === value.toLowerCase()}
                onClick={() => handleSelect(s.region)}
              >
                {s.state}
              </Dropdown.Item>
            ))}
          </StatesPane>
        )}
      </FlyoutMenu>
    </StyledDropdown>
  );
}
